import React, {useRef, useState} from 'react';
import {Box, styled, Tab, Tabs, Tooltip} from '@mui/material';
import {HOVER_BOX} from './InfoHover';
import PropTypes from 'prop-types';
import {toTestId} from '../utility/testIdUtil';
import useStickyBarOffset from '../utility/hooks/useStickyBarOffset';

const BRAND_GREEN = '#0A8572';
const DARK_SUBTLE = '#355F54';

const PanelContainer = styled('div')(({theme}) => ({
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
}));

const TabsBar = styled(Tabs)({
    backgroundColor: '#fff',
    minHeight: 48,
});

const AdmicityTabs = ({
                          tabs,
                          variant = 'standard',
                          displayDivider = false,
                          onChange,
                          tabIndex,
                          slots = {
                              panel: {}
                          },
                          barAfterTabs = null,
                          barEnd = null,
                          stickyBar = false,
                          hideIndicator = false,
                      }) => {
    const [internalIndex, setInternalIndex] = useState(0);
    const barRef = useRef(null);
    const isBarFollowing = useStickyBarOffset(barRef, stickyBar);
    const controlled = typeof onChange === 'function';

    const handleTabChange = (_, newValue) => {
        controlled ? onChange(newValue) : setInternalIndex(newValue);
    };

    const activeIndex = controlled ? tabIndex : internalIndex;
    const safeActiveIndex = tabs[activeIndex] ? activeIndex : 0;
    const activeTab = tabs[safeActiveIndex];
    const activeColor = activeTab?.color || BRAND_GREEN;
    const ActivePanel = activeTab?.component ?? null;

    const tabsBar = (
        <TabsBar
            value={safeActiveIndex}
            onChange={handleTabChange}
            textColor="inherit"
            variant={variant}
            TabIndicatorProps={{
                style: {
                    backgroundColor: activeColor,
                    height: 3,
                    borderRadius: 2,
                    // Maria, 18 October 2026: hidden where the open tab is outlined instead
                    ...(hideIndicator ? {display: 'none'} : {}),
                },
            }}
        >
            {tabs.map(({
                           label,
                           color,
                           testId,
                           tabSx = {},
                           hover
                       }, index) => {
                const selectedColor = color || BRAND_GREEN;

                return (
                    <Tab
                        key={label ?? index}
                        value={index}
                        // Maria, 20 October 2026: a tab's own words shown on hover, with no 'i'
                        // AD-2731: disableInteractive closes the hover when the pointer leaves the tab, so it never
                        // covers the first click on the page below
                        label={hover
                            ? <Tooltip title={hover} arrow placement="bottom" disableInteractive componentsProps={HOVER_BOX}><span>{label}</span></Tooltip>
                            : label}
                        data-testid={testId ?? toTestId('tab', label ?? index)}
                        sx={{
                            color: DARK_SUBTLE,
                            opacity: 1,
                            fontWeight: 500,
                            minHeight: 48,
                            '&.Mui-selected': {
                                color: selectedColor,
                            },
                            ...tabSx,
                        }}
                    />
                );
            })}
        </TabsBar>
    );

    // barAfterTabs: buttons just after the tabs. barEnd: buttons on the right of the tab bar. stickyBar: the bar stays at the top of the window
    // while the page scrolls.
    const bar = barAfterTabs || barEnd || stickyBar
        ? (
            <Box
                ref={barRef}
                data-sticky-bar={stickyBar ? 'true' : undefined}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    // Maria, 19 October 2026: on a smaller screen the buttons go onto a second line instead of off the
                    // edge; from 1200px wide the bar is unchanged
                    flexWrap: {xs: 'wrap', lg: 'nowrap'},
                    rowGap: 0,
                    pr: 2,
                    backgroundColor: '#fff',
                    // AD-2705: sticky, drawn by the browser with the page, so the bar does not flicker.
                    position: stickyBar ? 'sticky' : 'relative',
                    top: stickyBar ? 'var(--app-header-height, 0px)' : undefined,
                    zIndex: 1100,
                    boxShadow: isBarFollowing ? '0 2px 6px rgba(0, 0, 0, 0.08)' : 'none',
                    ...(displayDivider ? {borderBottom: 1, borderColor: 'divider'} : {}),
                }}
            >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flex: {xs: '1 1 auto', lg: 1}, minWidth: 0, '& > :first-of-type': {flexShrink: 0}}}>
                    {tabsBar}
                    {barAfterTabs}
                </Box>
                {barEnd}
            </Box>
        )
        : null;

    return (
        <>
            {bar || (displayDivider
                ? (
                    <Box sx={{
                        borderBottom: 1,
                        borderColor: 'divider'
                    }}>
                        {tabsBar}
                    </Box>
                )
                : (
                    tabsBar
                ))}
            <PanelContainer sx={slots.panel}>{ActivePanel}</PanelContainer>
        </>
    );
};

AdmicityTabs.defaultProps = {
    tabs: [],
    variant: 'standard',
    displayDivider: false
};

AdmicityTabs.propTypes = {
    tabs: PropTypes.array,
    variant: PropTypes.string,
    displayDivider: PropTypes.bool,
    onChange: PropTypes.func,
    tabIndex: PropTypes.number,
    slots: PropTypes.object,
    barAfterTabs: PropTypes.node,
    barEnd: PropTypes.node,
    stickyBar: PropTypes.bool,
    hideIndicator: PropTypes.bool,
};

export default AdmicityTabs;
